/**
 * ChatArea: 채팅 패널.
 *
 * v1 원본: RenewalComponents/Chat/ChatPresenter.js
 *   <div className="new_chat inst_tabs">
 *     <div className="chat_tabs">
 *       <div className="chat_selection"><h2>채팅</h2></div>
 *       <div className="right_btns"><button className="chat_closeBtn">닫기</button></div>
 *     </div>
 *     <div className="chatbox">...</div>
 *     <div className="inputbox">
 *       <textarea className="chatinput" />
 *       <button className="send_btn [active]"><span className="sp_rtc">전송</span></button>
 *     </div>
 *   </div>
 *
 * CSS 셀렉터: .rtc_new3 .sidebar.new2 .new_chat ...
 */
import React, { useEffect, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { shallowEqual } from 'react-redux';
import { chatActions } from '@/store/slices/chatSlice';
import { uiActions } from '@/store/slices/uiSlice';
import { DEFAULT_PROFILE, toDisplayProfileUrl } from '@/utils/profileUtils';
import { formatTime12h } from '@/utils/formatUtils';

interface ChatAreaProps {
  onSendMessage: (message: string) => void;
}

const ChatArea: React.FC<ChatAreaProps> = ({ onSendMessage }) => {
  const dispatch = useAppDispatch();
  const chat = useAppSelector((s) => s.chat);
  const localChat = useAppSelector((s) => ({
    jitsiId: s.local.jitsiId,
    nickname: s.local.nickname,
    userId: s.local.userId,
    profileUrl: s.local.profileUrl,
    avatarUrl: s.local.avatarUrl,
  }), shallowEqual);
  const participantsMap = useAppSelector((s) => s.participants.participants);
  const [inputText, setInputText] = useState('');
  const chatboxRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 새 메시지 수신 시 자동 스크롤
  useEffect(() => {
    const el = chatboxRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [chat.messages.length]);

  // 채팅 패널 열림/닫힘 상태 동기화
  useEffect(() => {
    dispatch(chatActions.setPanelOpen(true));
    return () => {
      dispatch(chatActions.setPanelOpen(false));
    };
  }, [dispatch]);

  const handleSend = () => {
    const text = inputText.trim();
    if (!text) return;
    onSendMessage(text);
    setInputText('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const LINE_HEIGHT = 21;
  const PADDING_V = 13;
  const MAX_LINES = 3;
  const MAX_HEIGHT = LINE_HEIGHT * MAX_LINES + PADDING_V;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, MAX_HEIGHT) + 'px';
  };

  // voice/JSON 타입 메시지 제외
  const textMessages = chat.messages.filter((m) => {
    try { const p = JSON.parse(m.message); return p.type !== 'voice'; } catch { return true; }
  });

  // senderId로 참여자 이름 조회
  const getSenderInfo = (senderId: string, senderName: string, msgProfileUrl?: string) => {
    if (senderId === localChat.jitsiId) {
      return {
        name: localChat.userId || localChat.nickname,
        profileUrl: toDisplayProfileUrl(localChat.profileUrl, localChat.avatarUrl) ?? '',
      };
    }
    const p = participantsMap[senderId];
    if (p) {
      return {
        name: p.displayName || p.nickname,
        profileUrl: toDisplayProfileUrl(p.profileUrl, p.avatarUrl) ?? '',
      };
    }
    return { name: senderName, profileUrl: msgProfileUrl ?? '' };
  };

  const hasSendableText = inputText.trim().length > 0;

  return (
    <div className="new_chat inst_tabs">
      <style>{`.chatinput::-webkit-scrollbar { display: none; }`}</style>

      {/* v1: .chat_tabs 헤더 */}
      <div className="chat_tabs">
        <div className="chat_selection">
          <h2>채팅</h2>
        </div>
        <div className="right_btns">
          <button
            type="button"
            className="chat_closeBtn"
            onClick={() => dispatch(uiActions.setOpenSideBar(false))}
          >
            <span className="blind">닫기</span>
          </button>
        </div>
      </div>

      {/* v1: .chatbox 메시지 영역 */}
      <div className="chatbox" style={{ bottom: 60 }} ref={chatboxRef}>
        {textMessages.map((msg) => {
          const isMe = msg.isMine ?? (msg.senderId === localChat.jitsiId);
          const { name, profileUrl } = getSenderInfo(msg.senderId, msg.senderName, msg.profileUrl);

          return isMe ? (
            <div key={msg.id} className="chatitem mychat">
              <div className="context_new">
                <span className="context_time">{formatTime12h(msg.timestamp)}</span>
                <p className="context_text">{msg.message}</p>
              </div>
            </div>
          ) : (
            <div key={msg.id} className="chatitem">
              <div className="profile">
                {profileUrl ? (
                  <img src={profileUrl} alt="프로필 이미지" />
                ) : (
                  <div className="common_coProfileBox common_coProfileBox--icon" style={{ background: '#c1cbdd', marginRight: 8 }}>
                    <div className="common_coProfileBox__imgBox" style={{ backgroundImage: `url("${DEFAULT_PROFILE}")` }} />
                  </div>
                )}
                <span className="name ellipsis">{name}</span>
              </div>
              <div className="context_new">
                <p className="context_text">{msg.message}</p>
                <span className="context_time">{formatTime12h(msg.timestamp)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* v1: .inputbox 입력 영역 */}
      <div className="inputbox">
        <textarea
          ref={textareaRef}
          className="chatinput"
          placeholder="메시지를 입력해주세요."
          value={inputText}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          rows={1}
          style={{ resize: 'none', overflowY: 'auto', scrollbarWidth: 'none' }}
        />
        <button
          type="button"
          className={`send_btn${hasSendableText ? ' active' : ''}`}
          onClick={handleSend}
          disabled={!hasSendableText}
        >
          <span className="sp_rtc">전송</span>
        </button>
      </div>
    </div>
  );
};

export default ChatArea;
